import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Shield, 
  Crown,
  TrendingUp,
  Settings,
  FileText,
  UserPlus,
  ChevronRight
} from 'lucide-react';
import { userAPI, timesheetAPI } from '../services/api';

const MOBILE_BREAKPOINT = 768;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, []);

  // Optimized: Single endpoint for dashboard stats (counts only)
  const { data: dashboardStats, isLoading: statsLoading } = useQuery(
    'super-admin-dashboard-stats',
    () => userAPI.getDashboardStats(),
    {
      select: (response) => response.data,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );

  // Optimized: Recent admins (top 5 only)
  const { data: recentAdmins, isLoading: adminsLoading } = useQuery(
    'super-admin-recent-admins',
    () => userAPI.getRecentAdmins(),
    {
      select: (response) => response.data || [],
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // Optimized: Recent timesheets (top 5 only)
  const { data: recentTimesheetsData, isLoading: timesheetsLoading } = useQuery(
    'super-admin-recent-timesheets',
    () => timesheetAPI.getRecentTimesheets(),
    {
      select: (response) => response.data?.timesheets || [],
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes (timesheets change more frequently)
      cacheTime: 5 * 60 * 1000,
    }
  );

  // Calculate dashboard stats from optimized endpoint
  const stats = [
    {
      name: 'Total Users',
      value: dashboardStats?.totalUsers || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      change: '+12%',
      changeType: 'positive'
    },
    {
      name: 'Active Admins',
      value: dashboardStats?.activeAdmins || 0,
      icon: Shield,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      change: '+2',
      changeType: 'positive'
    },
    {
      name: 'Total Employees',
      value: dashboardStats?.totalEmployees || 0,
      icon: UserPlus,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      change: '+5',
      changeType: 'positive'
    },
    {
      name: 'Pending Timesheets',
      value: dashboardStats?.pendingTimesheets || 0,
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      change: '+3',
      changeType: 'neutral'
    }
  ];

  // Show loading only for critical stats, allow progressive rendering
  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  // Separate mobile view
  if (isMobile) {
    return (
      <div className="space-y-4 pb-8">
        {/* Mobile Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm p-4"
        >
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-50 shrink-0">
              <Crown className="w-6 h-6 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base font-bold text-gray-900 break-words">Super Admin Dashboard</h1>
              <p className="text-xs text-gray-600 mt-0.5">
                System management and controls
              </p>
            </div>
          </div>
        </motion.div>

        {/* Mobile Stats - 2x2 grid, vertical layout so labels don't truncate */}
        <div className="grid grid-cols-2 gap-3">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm p-4 flex flex-col items-start gap-2"
            >
              <div className={`p-2.5 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <p className="text-xs font-medium text-gray-500 leading-tight break-words w-full">{stat.name}</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.change} vs last month</p>
            </motion.div>
          ))}
        </div>

        {/* Mobile System Management - vertical list */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <h2 className="px-4 py-3 text-base font-semibold text-gray-900 border-b border-gray-100">
            System Management
          </h2>
          <div className="divide-y divide-gray-100">
            <button
              type="button"
              onClick={() => navigate('/super-admin/users')}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 touch-manipulation"
            >
              <div className="p-2 rounded-lg bg-blue-50">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 break-words">User Management</h3>
                <p className="text-sm text-gray-500">Manage users and roles</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/super-admin/passwords')}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 touch-manipulation"
            >
              <div className="p-2 rounded-lg bg-blue-50">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 break-words">Password Management</h3>
                <p className="text-sm text-gray-500">Manage passwords</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/timesheets/review')}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 touch-manipulation"
            >
              <div className="p-2 rounded-lg bg-blue-50">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 break-words">Timesheet Review</h3>
                <p className="text-sm text-gray-500">Review all timesheets</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/super-admin/consolidated-export')}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 touch-manipulation"
            >
              <div className="p-2 rounded-lg bg-blue-50">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 break-words">Consolidation</h3>
                <p className="text-sm text-gray-500">Consolidated reports</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/super-admin/settings')}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 touch-manipulation"
            >
              <div className="p-2 rounded-lg bg-blue-50">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 break-words">System Settings</h3>
                <p className="text-sm text-gray-500">Configure options</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
          </div>
        </motion.div>

        {/* Mobile Recent Admins */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-base font-semibold text-gray-900">System Administrators</h2>
            <button
              type="button"
              onClick={() => navigate('/super-admin/users')}
              className="text-sm text-blue-600 font-medium py-1 touch-manipulation"
            >
              Manage All
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {adminsLoading ? (
              <div className="px-4 py-8 flex justify-center">
                <div className="spinner" />
              </div>
            ) : recentAdmins?.length > 0 ? (
              recentAdmins.map((admin) => (
                <div key={admin.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                    <span className="text-sm font-medium text-white">
                      {admin.firstName?.charAt(0)}{admin.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {admin.firstName} {admin.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{admin.email}</p>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">ADMIN</span>
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center">
                <UserPlus className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No administrators</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Mobile Recent Timesheets */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-base font-semibold text-gray-900">Recent Timesheets</h2>
            <button
              type="button"
              onClick={() => navigate('/admin/timesheets/review')}
              className="text-sm text-blue-600 font-medium py-1 touch-manipulation"
            >
              View All
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {timesheetsLoading ? (
              <div className="px-4 py-8 flex justify-center">
                <div className="spinner" />
              </div>
            ) : recentTimesheetsData?.length > 0 ? (
              recentTimesheetsData.map((timesheet) => (
                <button
                  key={timesheet.id}
                  type="button"
                  onClick={() => navigate(`/super-admin/timesheets/${timesheet.id}`)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left active:bg-gray-50 touch-manipulation"
                >
                  <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center shrink-0">
                    <span className="text-sm font-medium text-white">
                      {timesheet.employee?.firstName?.charAt(0)}{timesheet.employee?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {timesheet.employee?.firstName} {timesheet.employee?.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(timesheet.startDate).toLocaleDateString()} – {new Date(timesheet.endDate).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                    timesheet.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    timesheet.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                    timesheet.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {timesheet.status}
                  </span>
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center">
                <FileText className="mx-auto h-10 w-10 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No timesheets yet</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Mobile System Status */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-xl shadow-sm p-4"
        >
          <h2 className="text-base font-semibold text-gray-900 mb-3">System Status</h2>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <div className="h-2 w-2 bg-green-500 rounded-full shrink-0" />
              <div>
                <span className="text-sm font-medium text-green-800">Database</span>
                <p className="text-xs text-green-600">Connected</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <div className="h-2 w-2 bg-green-500 rounded-full shrink-0" />
              <div>
                <span className="text-sm font-medium text-green-800">API Services</span>
                <p className="text-xs text-green-600">Running</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
              <div className="h-2 w-2 bg-green-500 rounded-full shrink-0" />
              <div>
                <span className="text-sm font-medium text-green-800">Authentication</span>
                <p className="text-xs text-green-600">Active</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Desktop view
  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center space-x-3">
          <Crown className="w-8 h-8 text-yellow-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Complete system management and administrative controls
            </p>
          </div>
        </div>
      </motion.div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              <div className="text-right">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' : 
                  stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {stat.change}
                </span>
                <p className="text-xs text-gray-500">vs last month</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">System Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/super-admin/users')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <Users className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">User Management</h3>
            <p className="text-sm text-gray-500">Manage all users and roles</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/super-admin/passwords')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <Shield className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Password Management</h3>
            <p className="text-sm text-gray-500">Manage user passwords</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/admin/timesheets/review')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <FileText className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Timesheet Review</h3>
            <p className="text-sm text-gray-500">Review all timesheets</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/super-admin/consolidated-export')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <TrendingUp className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Consolidation</h3>
            <p className="text-sm text-gray-500">Generate consolidated timesheet reports</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/super-admin/settings')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <Settings className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">System Settings</h3>
            <p className="text-sm text-gray-500">Configure system options</p>
          </motion.button>
        </div>
      </motion.div>

      {/* Consolidated Timesheet Export Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Consolidation</h2>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/super-admin/consolidated-export')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <FileText className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Consolidated Timesheet Report</h3>
            <p className="text-sm text-gray-500">Generate consolidated PDF report for single or all projects</p>
          </motion.button>
        </div>
      </motion.div>

      {/* Recent Admins */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">System Administrators</h2>
          <button
            onClick={() => navigate('/super-admin/users')}
            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
          >
            Manage All
          </button>
        </div>
        <div className="divide-y divide-gray-200">
          {adminsLoading ? (
            <div className="px-6 py-8 text-center">
              <div className="spinner mx-auto"></div>
            </div>
          ) : recentAdmins?.length > 0 ? (
            recentAdmins.map((admin, index) => (
            <motion.div
              key={admin.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="px-6 py-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {admin.firstName?.charAt(0)}{admin.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {admin.firstName} {admin.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-500">ADMIN</span>
                </div>
              </div>
            </motion.div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <UserPlus className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No administrators</h3>
              <p className="mt-1 text-sm text-gray-500">Create admin accounts to manage the system.</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Recent Activity & System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Timesheets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm"
        >
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Recent Timesheets</h2>
            <button
              onClick={() => navigate('/admin/timesheets/review')}
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              View All
            </button>
          </div>
          <div className="divide-y divide-gray-200">
            {timesheetsLoading ? (
              <div className="px-6 py-8 text-center">
                <div className="spinner mx-auto"></div>
              </div>
            ) : recentTimesheetsData?.length > 0 ? (
              recentTimesheetsData.map((timesheet, index) => (
              <motion.div
                key={timesheet.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="px-6 py-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {timesheet.employee?.firstName?.charAt(0)}{timesheet.employee?.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {timesheet.employee?.firstName} {timesheet.employee?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(timesheet.startDate).toLocaleDateString()} - {new Date(timesheet.endDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      timesheet.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      timesheet.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                      timesheet.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {timesheet.status}
                    </span>
                    <button
                      onClick={() => navigate(`/super-admin/timesheets/${timesheet.id}`)}
                      className="text-xs text-primary-600 hover:text-primary-500 font-medium px-2 py-1 hover:bg-primary-50 rounded"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </motion.div>
              ))
            ) : (
              <div className="px-6 py-8 text-center">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No timesheets</h3>
                <p className="mt-1 text-sm text-gray-500">No timesheets have been submitted yet.</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* System Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <h2 className="text-lg font-medium text-gray-900 mb-4">System Status</h2>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-green-800">Database</span>
              </div>
              <p className="text-sm text-green-600 mt-1">Connected</p>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-green-800">API Services</span>
              </div>
              <p className="text-sm text-green-600 mt-1">Running</p>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm font-medium text-green-800">Authentication</span>
              </div>
              <p className="text-sm text-green-600 mt-1">Active</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;

